import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Image as ImageIcon, Loader2, RefreshCw, Video, X } from 'lucide-react';
import EZOPENPlayer from '../ui/EZOPENPlayer';
import { cameraService } from '../../services';
import { formatEventDate, getEventPlaybackTimes } from '../../utils/eventTime';
import type { Camera, Event } from '../../types';
import './EventPlaybackModal.css';

interface Props {
  event: Event;
  locationTimezone?: string | null;
  onClose: () => void;
  onSnapshot?: () => void;
}

/** Streams recorder/cloud recordings through EZOPEN without waiting for an archived clip. */
const EventPlaybackModal: React.FC<Props> = ({ event, locationTimezone, onClose, onSnapshot }) => {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const [camera, setCamera] = useState<Camera | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [storage, setStorage] = useState<1 | 2>(2);
  const [buffer, setBuffer] = useState(60);
  const timeZone = event.location_timezone || locationTimezone;
  const options = { timeZone, naiveIsLocal: event.event_source === 'cloud_alarm' };
  let times: { start: string; end: string } | null = null;
  let eventTime = '';
  try {
    times = getEventPlaybackTimes(event.created_date || '', options, buffer);
    eventTime = getEventPlaybackTimes(event.created_date || '', options, 0).start;
  } catch { /* An invalid event time is shown below without mounting the SDK. */ }

  // Canonical API timestamps identify an instant. Keep provider searches in UTC
  // while EZOPEN's local-recording URL uses the recorder's wall clock.
  const raw = event.created_date || '';
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const utcInstant = hasOffset ? new Date(raw) : event.event_source !== 'cloud_alarm' ? new Date(`${raw}Z`) : null;
  const searchStart = utcInstant && Number.isFinite(utcInstant.getTime()) ? new Date(utcInstant.getTime() - buffer * 1000).toISOString() : undefined;
  const searchEnd = utcInstant && Number.isFinite(utcInstant.getTime()) ? new Date(utcInstant.getTime() + buffer * 1000).toISOString() : undefined;
  const cameraId = event.camera_id || Number(event.event_camera);

  useEffect(() => {
    let active = true;
    setCamera(null); setError('');
    const load = async () => {
      try {
        if (!Number.isInteger(cameraId) || cameraId <= 0) throw new Error('This event has no linked camera for playback.');
        const result = await cameraService.getCameraById(cameraId);
        if (!result.cloud_provider) throw new Error('Configure cloud access in this camera’s settings to enable recorder playback.');
        if (active) setCamera(result);
      } catch (err) { if (active) setError(err instanceof Error ? err.message : 'Unable to load the camera. Try connecting again.'); }
    };
    void load();
    return () => { active = false; };
  }, [cameraId, retry]);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; close.current?.focus();
    const keydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) { e.preventDefault(); onCloseRef.current(); }
      if (e.key !== 'Tab') return;
      const items = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), [tabindex="0"]') || []);
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown); document.body.style.overflow = overflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const failure = !times ? 'The event timestamp is unavailable. A valid event time is needed to find the recording.' : error;
  return createPortal(<div className="event-playback" ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId}>
    <header className="event-playback__header">
      <div className="event-playback__identity"><span className="event-playback__icon"><Video size={21} /></span><div>
        <p className="event-playback__eyebrow">Camera recording</p>
        <h2 id={titleId}>Event #{event.event_id} playback</h2>
        <p className="event-playback__camera">{event.camera_name || camera?.name || `Camera ${cameraId || 'unavailable'}`}{event.location_name && ` · ${event.location_name}`}</p>
      </div></div>
      <div className="event-playback__header-actions">
        {onSnapshot && <button type="button" className="event-playback__button" onClick={onSnapshot}><ImageIcon size={16} />Snapshot</button>}
        <button ref={close} type="button" className="event-playback__button event-playback__close" aria-label="Close camera playback" onClick={onClose}><X size={21} /></button>
      </div>
    </header>
    <main className="event-playback__main">
      <div className="event-playback__options">
        <div className="event-playback__event-time"><span>Event time</span><strong>{formatEventDate(event.created_date, { ...options, includeSeconds: true })}</strong><small>{timeZone || 'Your local timezone'}</small></div>
        <div className="event-playback__selectors">
          <label>Recording source<select value={storage} onChange={e => setStorage(Number(e.target.value) as 1 | 2)}>
            <option value={2}>Local recorder / SD card</option><option value={1}>Cloud recording</option>
          </select></label>
          <label>Time window<select value={buffer} onChange={e => setBuffer(Number(e.target.value))}>
            <option value={60}>2 minutes · ±1 minute</option><option value={120}>4 minutes · ±2 minutes</option><option value={300}>10 minutes · ±5 minutes</option>
          </select></label>
          <button type="button" className="event-playback__button" onClick={() => setRetry(value => value + 1)} aria-label="Retry camera connection"><RefreshCw size={16} /><span>Reconnect</span></button>
        </div>
      </div>
      <div className="event-playback__stage">
        {failure ? <div className="event-playback__placeholder" role="alert"><AlertCircle size={30} /><h3>Playback unavailable</h3><p>{failure}</p>
          {!!times && <button type="button" className="event-playback__button" onClick={() => setRetry(value => value + 1)}>Try again</button>}</div>
          : !camera ? <div className="event-playback__placeholder" role="status"><Loader2 size={30} className="animate-spin" /><h3>Connecting to camera</h3><p>Loading access for this event’s recording…</p></div>
            : <EZOPENPlayer key={`${camera.camera_id}:${storage}:${buffer}:${retry}`} camera={{ id: camera.camera_id,
                cloud_serial_no: camera.cloud_serial_no, cloud_channel_no: camera.cloud_channel_no, cloud_provider: camera.cloud_provider }}
                cameraName={event.camera_name || camera.name} mode="playback" startTime={times!.start} endTime={times!.end}
                searchStartTime={searchStart} searchEndTime={searchEnd} playbackTimezone={timeZone} initialStorageType={storage} eventTime={eventTime}
                fillContainer showControls showAudioControls />}
      </div>
      <footer className="event-playback__footer"><span><span className="event-playback__marker" />The amber marker shows when the event occurred.</span><span>Playback depends on the camera’s recording schedule and retention.</span></footer>
    </main>
  </div>, document.body);
};

export default EventPlaybackModal;
